import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { toast } from 'sonner';
import {
  LayoutGrid, History, Settings2, BarChart2, Store, Globe, Lock, Radio, ExternalLink, Copy, Check,
  Download, ChevronDown, Pencil, Trash2, RotateCcw, Clock, ShieldCheck, ArrowLeft, Loader2, Camera, ImagePlus,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { authFetch } from '@/lib/authFetch';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { iconDataUri } from '@/lib/sitePages';
import { isOwnIcon } from '@/lib/siteIcon';
import DownloadMenu from '@/components/generation/DownloadMenu';
import SiteIconEditor from '@/components/generation/SiteIconEditor';
import SellSiteDialog from '@/components/marketplace/SellSiteDialog';
import { myListings, LISTING_STATUS } from '@/lib/marketplace';
import { categoryLabel, sellerShare, usd, MARKETPLACE, percent } from '@/config/earnings';

// ─── A site's own dashboard ───────────────────────────────────────────────────
// The Dashboard tab between Preview and Code. Everything about the ONE site
// that is open, in this order: its overview, selling it, its versions, its
// activity and its settings. The icon can be changed from Overview (tap the
// icon) and Settings → Site icon (SiteIconEditor).
//
// These used to be a "This site" group in the account sidebar, beside "My
// sites" and the account settings — so the same sidebar held two different
// subjects, and people could not tell which items were about the site and
// which about their account. The account lives on the account dashboard now;
// this is only ever about the site, and says so with tabs across the top.

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function SectionCard({ title = '', description = '', children = null }) {
  return (
    <div className="bg-zinc-900/70 border border-zinc-800 rounded-2xl p-5">
      {title && <h4 className="text-sm font-semibold text-white mb-1">{title}</h4>}
      {description && <p className="text-zinc-500 text-xs mb-4 leading-relaxed">{description}</p>}
      {children}
    </div>
  );
}

/**
 * The site's icon. With `onEdit` it is also the way to change it: a camera
 * badge says so on a phone, where there is no hover to reveal it.
 */
function AppIcon({ iconSvg, size = 'w-16 h-16', onEdit = null }) {
  // Rendered as an <img> data URI so embedded scripts can never execute.
  const src = iconDataUri(iconSvg);
  // The owner's own picture fills its square; a drawn mark keeps its margin.
  const own = isOwnIcon(iconSvg);
  const face = src ? (
    <div className={`${size} rounded-2xl bg-zinc-800 border border-zinc-700 overflow-hidden shrink-0 ${own ? '' : 'p-2'}`}>
      <img src={src} alt="App icon" className={`w-full h-full ${own ? 'object-cover' : 'object-contain'}`} />
    </div>
  ) : (
    <div className={`${size} rounded-2xl bg-gradient-to-br from-orange-500/20 to-orange-600/5 border border-orange-500/20 shrink-0`} />
  );
  if (!onEdit) return face;
  return (
    <button type="button" onClick={onEdit} aria-label="Change the site icon" title="Change the site icon"
      className="group/icon relative shrink-0 rounded-2xl outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950">
      {face}
      <span aria-hidden="true" className="absolute inset-0 rounded-2xl bg-black/50 grid place-items-center opacity-0 group-hover/icon:opacity-100 transition-opacity">
        <Camera className="w-5 h-5 text-snow" />
      </span>
      <span aria-hidden="true" className="absolute -bottom-1.5 -right-1.5 w-6 h-6 rounded-full bg-orange-500 text-ink grid place-items-center ring-[3px] ring-zinc-950 shadow-md transition-transform group-hover/icon:scale-110">
        <Camera className="w-3 h-3" />
      </span>
    </button>
  );
}

function StatusChip({ isPublic, status, hasContent = false }) {
  // A site with content is finished whatever the status column says.
  if (status && status !== 'completed' && !hasContent) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-yellow-400 bg-yellow-500/10 border border-yellow-500/20 rounded-full px-2.5 py-0.5">
        <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" /> {status}
      </span>
    );
  }
  return isPublic ? (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2.5 py-0.5">
      <Radio className="w-3 h-3" /> Live
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-zinc-400 bg-zinc-800 border border-zinc-700 rounded-full px-2.5 py-0.5">
      <Lock className="w-3 h-3" /> Private
    </span>
  );
}

// ── Overview ──────────────────────────────────────────────────────────────────

function OverviewSection({ meta, websiteCode, shareUrl, onOpenApp, onCopyLink, copied, onDownloadZip, canExport, unlock, onRename, onToggleVisibility, savingVisibility, onShare, onEditIcon }) {
  const [editing, setEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const title = meta?.title || websiteCode.title || 'Untitled Website';
  const description = meta?.prompt || '';

  const commitEdit = () => {
    setEditing(false);
    const next = titleDraft.trim();
    if (next && next !== title) onRename(next);
  };

  return (
    <div className="space-y-5">
      <div className="space-y-4">
        <div className="flex items-start gap-4">
          <AppIcon iconSvg={meta?.icon_svg || websiteCode.icon} onEdit={onEditIcon} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 group">
              {editing ? (
                <input autoFocus value={titleDraft} onChange={e => setTitleDraft(e.target.value)} onBlur={commitEdit}
                  onKeyDown={e => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditing(false); }}
                  aria-label="Site name"
                  className="text-lg sm:text-xl font-bold text-white bg-transparent border-b border-orange-500 outline-none flex-1 min-w-0" />
              ) : (
                <>
                  <h3 className="text-lg sm:text-xl font-bold text-white truncate">{title}</h3>
                  {/* Always shown on a phone: there is no hover to reveal it. */}
                  <button onClick={() => { setTitleDraft(title); setEditing(true); }} title="Rename" aria-label="Rename"
                    className="sm:opacity-0 sm:group-hover:opacity-100 focus-visible:opacity-100 transition-opacity text-zinc-500 hover:text-white shrink-0 p-1 -m-1">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
              <div className="ml-auto shrink-0">
                <StatusChip isPublic={!!meta?.is_public} status={meta?.status}
                  hasContent={String(meta?.html_content || websiteCode?.html || '').trim().length > 0} />
              </div>
            </div>
            {description && <p className="text-zinc-400 text-sm leading-relaxed line-clamp-2 mt-1">{description}</p>}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1.5 text-xs text-zinc-600">
              {meta?.created_at && <span>Created {formatDate(meta.created_at)}</span>}
              {meta?.version && <span className="text-zinc-500">v{meta.version}</span>}
              {websiteCode?.pages?.length > 0 && <span>{websiteCode.pages.length} page{websiteCode.pages.length === 1 ? '' : 's'}</span>}
            </div>
          </div>
        </div>

        {/* Full-width buttons on a phone, two to a row; in a line under the
            name from sm up (pl-20 = the icon and its gap). */}
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:pl-20">
          <Button size="sm" onClick={onShare} className="col-span-2 h-10 sm:h-8 px-3.5 text-sm sm:text-xs bg-orange-500 hover:bg-orange-400 text-ink rounded-xl sm:rounded-lg gap-1.5">
            <Globe className="w-4 h-4 sm:w-3.5 sm:h-3.5" /> {meta?.is_public ? 'Share link' : 'Publish'}
          </Button>
          <Button size="sm" variant="outline" onClick={onOpenApp} className="h-10 sm:h-8 px-3.5 text-sm sm:text-xs border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800 hover:text-white rounded-xl sm:rounded-lg gap-1.5">
            <ExternalLink className="w-4 h-4 sm:w-3.5 sm:h-3.5" /> Open preview
          </Button>
          <DownloadMenu onDownload={onDownloadZip} canExport={canExport} unlock={unlock} align="start">
            <Button size="sm" variant="outline" disabled={!websiteCode.html} className="h-10 sm:h-8 px-3.5 text-sm sm:text-xs border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800 hover:text-white data-[state=open]:bg-zinc-800 data-[state=open]:text-white rounded-xl sm:rounded-lg gap-1.5">
              <Download className="w-4 h-4 sm:w-3.5 sm:h-3.5" /> Download <ChevronDown className="w-3.5 h-3.5 opacity-60" />
            </Button>
          </DownloadMenu>
        </div>
      </div>

      <SectionCard title="Visibility" description="When live, anyone with the link can view your site. Private sites are only visible to you.">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Switch checked={!!meta?.is_public} onCheckedChange={onToggleVisibility} disabled={savingVisibility || !meta} aria-label="Site is live" />
            <div className="min-w-0">
              <p className="text-sm text-white font-medium">{meta?.is_public ? 'Live — anyone with the link' : 'Private — only you'}</p>
              {/* !! because MySQL hands is_public back as 0/1, and a bare 0 is
                  something React renders. */}
              {!!meta?.is_public && (
                <button onClick={onCopyLink} className="text-xs text-orange-400 hover:text-orange-300 truncate block max-w-full transition-colors" title={shareUrl}>
                  {shareUrl}
                </button>
              )}
            </div>
          </div>
          {savingVisibility && <span className="text-[11px] text-zinc-500 shrink-0 animate-pulse">Saving…</span>}
        </div>
      </SectionCard>

      <SectionCard title="Share your creation" description="Send the link to clients, friends, or your socials.">
        <div className="flex gap-2">
          <div className="flex-1 flex items-center gap-2 bg-zinc-800/80 border border-zinc-700 rounded-lg px-3 py-2 min-w-0">
            <Globe className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
            <span className="text-xs text-zinc-300 truncate">{shareUrl}</span>
          </div>
          <Button size="sm" onClick={onCopyLink} className="h-9 px-4 text-xs bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-200 rounded-lg gap-1.5 shrink-0">
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />} Copy
          </Button>
        </div>
      </SectionCard>
    </div>
  );
}

// ── Versions ──────────────────────────────────────────────────────────────────

function VersionsSection({ versions, currentVersion, onRevert }) {
  if (!versions?.length) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-16 px-8">
        <div className="w-14 h-14 rounded-2xl bg-zinc-900 border border-zinc-800 flex items-center justify-center mb-4">
          <Clock className="w-6 h-6 text-zinc-500" />
        </div>
        <h3 className="text-white font-semibold mb-1.5">No previous versions yet</h3>
        <p className="text-zinc-500 text-sm max-w-sm leading-relaxed">
          Every time you change your site, the version before is kept here so you can go back to it.
        </p>
      </div>
    );
  }
  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <span className="text-sm text-zinc-300">Current:</span>
        <span className="text-xs font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2.5 py-0.5">
          v{currentVersion || (versions[0]?.version ?? 0) + 1}
        </span>
      </div>
      <div className="space-y-2.5">
        {versions.map(v => (
          <div key={v.id} className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors flex items-start gap-4">
            <div className="w-9 h-9 rounded-lg bg-zinc-800 border border-zinc-700 flex items-center justify-center shrink-0">
              <span className="text-xs font-bold text-orange-400">v{v.version}</span>
            </div>
            <div className="flex-1 min-w-0">
              {v.prompt && <p className="text-sm text-zinc-300 line-clamp-2 leading-relaxed">{v.prompt}</p>}
              <p className="text-[11px] text-zinc-600 mt-1">{formatDate(v.created_at)}</p>
            </div>
            <Button size="sm" onClick={() => onRevert(v)} className="h-9 sm:h-7 px-3 text-xs sm:text-[11px] bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white rounded-lg border border-zinc-700 shrink-0 gap-1.5">
              <RotateCcw className="w-3 h-3" /> Restore
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Settings ──────────────────────────────────────────────────────────────────

function SettingsSection({ meta, icon, onEditIcon, onRename, onToggleVisibility, savingVisibility, onDelete, deleting }) {
  const [nameDraft, setNameDraft] = useState(meta?.title || '');
  useEffect(() => { setNameDraft(meta?.title || ''); }, [meta?.title]);
  const own = isOwnIcon(icon);

  return (
    <div className="space-y-5">
      <SectionCard title="Site icon" description="Shown in the browser tab, on your site's card and in every download. Use a photo or your logo.">
        <div className="flex items-center gap-4">
          <AppIcon iconSvg={icon} onEdit={onEditIcon} />
          <div className="min-w-0 space-y-2">
            <p className="text-xs text-zinc-400">{own ? 'Your own picture.' : 'Drawn for your site when it was built.'}</p>
            <button type="button" onClick={onEditIcon} className="inline-flex items-center h-9 px-4 text-xs font-medium bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-100 rounded-lg gap-1.5 transition-colors">
              <ImagePlus className="w-3.5 h-3.5" /> {own ? 'Change picture' : 'Upload a photo'}
            </button>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Site name" description="Shown in your dashboard, browser tab, and share cards.">
        <div className="flex gap-2">
          <input value={nameDraft} onChange={e => setNameDraft(e.target.value)} placeholder="My awesome site" aria-label="Site name"
            className="flex-1 bg-zinc-800/80 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 outline-none focus:border-orange-500/50 transition-colors" />
          <Button size="sm" onClick={() => { const t = nameDraft.trim(); if (t && t !== meta?.title) onRename(t); }}
            disabled={!nameDraft.trim() || nameDraft.trim() === meta?.title}
            className="h-9 px-4 text-xs bg-orange-500 hover:bg-orange-400 disabled:opacity-40 text-ink rounded-lg shrink-0">
            Save
          </Button>
        </div>
      </SectionCard>

      <SectionCard title="Visibility" description="Control who can open your site's public link.">
        <div className="flex items-center gap-3">
          <Switch checked={!!meta?.is_public} onCheckedChange={onToggleVisibility} disabled={savingVisibility || !meta} aria-label="Site is live" />
          <span className="text-sm text-zinc-300">{meta?.is_public ? 'Live — anyone with the link can view' : 'Private — only you can view'}</span>
        </div>
      </SectionCard>

      <SectionCard title="BuildersLab badge" description='The "Built with BuildersLab" badge appears on sites built on the free plan. Every paid plan removes it automatically.'>
        <div className="flex items-center gap-2 text-xs text-zinc-400">
          <ShieldCheck className="w-4 h-4 text-zinc-500" />
          {meta?.purchased_at ? 'This site was bought outright — it has no badge.' : 'Managed by your plan.'}
        </div>
      </SectionCard>

      <div className="bg-red-500/[0.04] border border-red-500/20 rounded-2xl p-5">
        <h4 className="text-sm font-semibold text-red-400 mb-1">Danger zone</h4>
        <p className="text-zinc-500 text-xs mb-4 leading-relaxed">
          Deleting this site permanently removes it, its code, its published link and all version history. This cannot be undone.
        </p>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" disabled={deleting || !meta} className="h-8 px-3.5 text-xs bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg gap-1.5">
              <Trash2 className="w-3.5 h-3.5" /> {deleting ? 'Deleting…' : 'Delete this site'}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-zinc-900 border-zinc-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Delete “{meta?.title || 'this site'}”?</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                The site, its generated code, and all version history will be permanently deleted. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700 hover:text-white">Keep it</AlertDialogCancel>
              <AlertDialogAction onClick={onDelete} className="bg-red-500 hover:bg-red-400 text-white">Delete permanently</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

// ── Activity ──────────────────────────────────────────────────────────────────

/** "index.html" → "Home", "our-menu.html" → "our-menu". */
function pageLabel(path) {
  const leaf = String(path || '').replace(/\.html?$/i, '');
  return !leaf || leaf === 'index' ? 'Home' : leaf;
}

const dayLabel = (day) => new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });

/**
 * Daily views as bars. Pointing at the chart, or running a finger along it,
 * picks a day and shows its numbers above. A `title` per bar only ever worked
 * with a mouse, and 90 days of bars are too thin to tap one at a time, so the
 * day is read from where the pointer is across the whole chart.
 */
function VisitBars({ series }) {
  const [picked, setPicked] = useState(null);
  const max = Math.max(1, ...series.map(d => d.views));
  const total = series.reduce((n, d) => n + d.views, 0);
  const pick = (e) => {
    const box = e.currentTarget.getBoundingClientRect();
    if (!box.width) return;
    setPicked(Math.min(series.length - 1, Math.max(0, Math.floor(((e.clientX - box.left) / box.width) * series.length))));
  };
  const day = picked === null ? null : series[picked];
  return (
    <div>
      <p className="h-5 mb-1.5 text-xs text-zinc-400 tabular-nums truncate" aria-live="polite">
        {day ? (
          <>
            <span className="text-white font-semibold">{picked === series.length - 1 ? 'Today' : dayLabel(day.day)}</span>
            {' · '}{day.visitors} visitor{day.visitors === 1 ? '' : 's'} · {day.views} view{day.views === 1 ? '' : 's'}
          </>
        ) : <span className="text-zinc-600">Point at a day, or run a finger along the chart.</span>}
      </p>
      <div
        role="img"
        aria-label={`${total} page views over the last ${series.length} days`}
        className="h-28 flex items-end gap-[2px] cursor-crosshair"
        // pan-y: a vertical swipe still scrolls the page, a sideways one scrubs.
        style={{ touchAction: 'pan-y' }}
        onPointerDown={pick}
        onPointerMove={(e) => { if (e.pointerType === 'mouse' || e.buttons) pick(e); }}
        onPointerLeave={(e) => { if (e.pointerType === 'mouse') setPicked(null); }}
      >
        {series.map((d, i) => (
          <div
            key={d.day}
            title={`${dayLabel(d.day)}: ${d.visitors} visitor${d.visitors === 1 ? '' : 's'}, ${d.views} view${d.views === 1 ? '' : 's'}`}
            className={`flex-1 min-w-0 rounded-t-sm transition-colors ${picked === i ? 'bg-orange-400' : picked === null ? 'bg-orange-500/70' : 'bg-orange-500/40'}`}
            style={{ height: `${Math.max(d.views ? 6 : 2, Math.round((d.views / max) * 100))}%`, opacity: d.views ? 1 : 0.25 }}
          />
        ))}
      </div>
      <div className="mt-1.5 flex justify-between text-[10px] text-zinc-600">
        <span>{dayLabel(series[0].day)}</span>
        <span>Today</span>
      </div>
    </div>
  );
}

const VISIT_RANGES = [7, 30, 90];

/**
 * What this site has done: visitors and page views, enquiries, and whether it
 * is live. Visits are counted by a beacon in the published page (server
 * services/siteVisits.js). There are no cookies: a visitor is one browser on
 * one day. The in-app preview and downloaded code never count.
 */
function ActivitySection({ websiteId, meta }) {
  const [subs, setSubs] = useState(null);
  const [failed, setFailed] = useState(false);
  const [range, setRange] = useState(30);
  const [visits, setVisits] = useState(null);   // null = loading
  const [visitsFailed, setVisitsFailed] = useState(false);

  useEffect(() => {
    if (!websiteId) { setSubs([]); return undefined; }
    let cancelled = false;
    authFetch(`/api/websites/${websiteId}/submissions`)
      .then(({ submissions }) => { if (!cancelled) setSubs(submissions || []); })
      .catch(() => { if (!cancelled) { setSubs([]); setFailed(true); } });
    return () => { cancelled = true; };
  }, [websiteId]);

  useEffect(() => {
    if (!websiteId) return undefined;
    let cancelled = false;
    setVisits(null);
    setVisitsFailed(false);
    authFetch(`/api/websites/${websiteId}/visits?days=${range}`)
      .then((data) => { if (!cancelled) setVisits(data); })
      .catch(() => { if (!cancelled) setVisitsFailed(true); });
    return () => { cancelled = true; };
  }, [websiteId, range]);

  const live = visits ? visits.published : !!meta?.is_public;
  const tiles = [
    { label: 'Visitors', value: visits ? visits.totals.visitors.toLocaleString() : '—', hint: `last ${range} days` },
    { label: 'Page views', value: visits ? visits.totals.views.toLocaleString() : '—', hint: `last ${range} days` },
    { label: 'Enquiries', value: subs === null ? '—' : subs.length, hint: 'from forms on this site' },
    { label: 'Status', value: live ? 'Live' : 'Private', hint: live ? 'anyone with the link' : 'only you' },
  ];

  return (
    <div className="space-y-5">
      <p className="text-zinc-500 text-xs">What this site has done since you published it.</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {tiles.map(t => (
          <div key={t.label} className="bg-zinc-900/70 border border-zinc-800 rounded-2xl p-4">
            <p className="text-[10px] uppercase tracking-widest text-zinc-600 font-semibold">{t.label}</p>
            <p className="text-2xl font-bold text-white mt-1 tabular-nums">{t.value}</p>
            <p className="text-[11px] text-zinc-600 mt-0.5 leading-snug">{t.hint}</p>
          </div>
        ))}
      </div>

      <SectionCard>
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <h4 className="text-sm font-semibold text-white mb-1">Visitors</h4>
            <p className="text-zinc-500 text-xs leading-relaxed">Views of your published site, day by day.</p>
          </div>
          <div className="flex rounded-full border border-zinc-800 p-0.5" role="group" aria-label="Time range">
            {VISIT_RANGES.map(d => (
              <button
                key={d}
                type="button"
                onClick={() => setRange(d)}
                aria-pressed={range === d}
                className={`h-7 px-3 rounded-full text-xs font-semibold transition-colors ${range === d ? 'bg-white text-zinc-950' : 'text-zinc-400 hover:text-white'}`}
              >
                {d} days
              </button>
            ))}
          </div>
        </div>

        {visitsFailed ? (
          <p className="text-xs text-zinc-500">Could not load visits just now.</p>
        ) : !visits ? (
          <div className="h-28 rounded-xl bg-zinc-800/40 animate-pulse" aria-hidden="true" />
        ) : !visits.published && visits.allTime.views === 0 ? (
          <p className="text-xs text-zinc-500 leading-relaxed">
            Visits are counted once your site is live. Publish it from Overview or Share, and every visit shows up here.
          </p>
        ) : (
          <>
            <VisitBars series={visits.series} />
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="text-xs text-zinc-400 space-y-1">
                <p><span className="text-white font-semibold tabular-nums">{visits.today.visitors}</span> visitor{visits.today.visitors === 1 ? '' : 's'} today, <span className="tabular-nums">{visits.today.views}</span> view{visits.today.views === 1 ? '' : 's'}</p>
                <p><span className="text-white font-semibold tabular-nums">{visits.allTime.views.toLocaleString()}</span> views since you published it</p>
              </div>
              {visits.pages.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-600 font-semibold mb-1.5">Top pages</p>
                  <ul className="space-y-1">
                    {visits.pages.map(pg => (
                      <li key={pg.path} className="flex items-center justify-between gap-3 text-xs">
                        <span className="text-zinc-300 truncate">{pageLabel(pg.path)}</span>
                        <span className="text-zinc-500 tabular-nums shrink-0">{pg.views.toLocaleString()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </>
        )}
      </SectionCard>

      <SectionCard title="Recent enquiries" description="Anything submitted through a form on this site.">
        {subs === null && <p className="text-xs text-zinc-600">Loading…</p>}
        {subs !== null && subs.length === 0 && (
          <p className="text-xs text-zinc-500 leading-relaxed">
            {failed ? 'Could not load enquiries just now.' : 'Nothing yet. Once your site is live, anything sent through its forms shows up here and in your email.'}
          </p>
        )}
        {subs !== null && subs.length > 0 && (
          <div className="flex flex-col gap-2">
            {subs.slice(0, 6).map(s => (
              <div key={s.id} className="flex items-baseline justify-between gap-3 bg-zinc-950/60 border border-zinc-800 rounded-lg px-3 py-2">
                <span className="text-xs text-zinc-300 truncate">{s.kind === 'mailing' ? 'Mailing list signup' : 'Contact form'}</span>
                <span className="text-[11px] text-zinc-600 shrink-0">{formatDate(s.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
      <p className="text-[11px] text-zinc-600 leading-relaxed">
        Counted without cookies: a visitor is one browser on one day. Your own previews here, and downloaded code, are not counted.
      </p>
    </div>
  );
}

// ── Sell ──────────────────────────────────────────────────────────────────────

function SellSection({ websiteId, meta, websiteCode }) {
  const [listing, setListing] = useState(undefined);   // undefined = loading, null = not listed
  const [dialog, setDialog] = useState(false);
  const boughtHere = !!meta?.purchase_reference && /^(flutterwave|nowpayments):/.test(meta.purchase_reference);

  const load = useCallback(() => {
    myListings()
      .then(({ listings }) => setListing((listings || []).find(l => l.website_id === websiteId && l.status !== 'removed') || null))
      .catch(() => setListing(null));
  }, [websiteId]);
  useEffect(() => { load(); }, [load]);

  if (listing === undefined) return <div className="flex items-center gap-2 text-sm text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> Checking…</div>;

  const site = { id: websiteId, title: meta?.title || websiteCode.title };
  const status = listing ? LISTING_STATUS[listing.status] : null;

  return (
    <div className="space-y-5">
      {listing ? (
        <SectionCard>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-white font-semibold truncate">{listing.title}</p>
              <p className="text-xs text-zinc-500 mt-0.5">{categoryLabel(listing.category)} · {usd(listing.price_usd)} · {listing.sales_count} sold · earned {usd(listing.earned_usd)}</p>
            </div>
            <span className={`text-[11px] font-semibold rounded-full border px-2.5 py-0.5 ${status?.tone}`}>{status?.label}</span>
          </div>
          {listing.status === 'rejected' && listing.rejection_reason && (
            <p className="mt-3 text-xs text-red-300/90">{listing.rejection_reason} Edit the listing to send it for review again.</p>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to={`/marketplace/${listing.id}`} className="h-8 px-3 rounded-lg border border-zinc-700 text-xs font-semibold text-zinc-300 hover:text-white inline-flex items-center gap-1.5">
              <ExternalLink className="w-3.5 h-3.5" /> View listing
            </Link>
            {listing.status !== 'sold' && (
              <button onClick={() => setDialog(true)} className="h-8 px-3 rounded-lg border border-zinc-700 text-xs font-semibold text-zinc-300 hover:text-white inline-flex items-center gap-1.5">
                <Pencil className="w-3.5 h-3.5" /> Edit listing
              </button>
            )}
          </div>
        </SectionCard>
      ) : boughtHere ? (
        <SectionCard title="Bought on the marketplace" description="Sites bought on the marketplace are yours to use and change, but cannot be resold." />
      ) : (
        <div className="rounded-2xl border border-orange-500/25 bg-orange-500/[0.06] p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <span className="w-11 h-11 rounded-2xl bg-orange-500/15 grid place-items-center shrink-0"><Store className="w-5 h-5 text-orange-400" /></span>
            <div>
              <p className="text-white font-bold text-lg">Sell this site on the marketplace</p>
              <p className="mt-1 text-sm text-zinc-400 leading-relaxed max-w-lg">
                Buyers get their own copy, with the full code. You keep this one and earn {percent(1 - MARKETPLACE.commissionRate)} of
                every sale — {usd(sellerShare(49))} on a {usd(49)} listing.
              </p>
              <button onClick={() => setDialog(true)} disabled={!websiteCode?.html}
                className="mt-4 h-11 sm:h-10 w-full sm:w-auto px-5 rounded-full bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-ink text-sm font-bold transition-colors">
                List this site
              </button>
            </div>
          </div>
        </div>
      )}
      <SellSiteDialog open={dialog} onClose={() => setDialog(false)} site={listing ? null : site} listing={listing || null} onSaved={load} />
    </div>
  );
}

// ── The tab ───────────────────────────────────────────────────────────────────

const SECTIONS = [
  { key: 'overview', label: 'Overview', icon: LayoutGrid },
  { key: 'sell',     label: 'Sell',     icon: Store },
  { key: 'versions', label: 'Versions', icon: History },
  { key: 'activity', label: 'Activity', icon: BarChart2 },
  { key: 'settings', label: 'Settings', icon: Settings2 },
];

/**
 * The section tabs: one tablist at every width, drawn two ways. On a phone,
 * five equal tiles, every one on screen — the strip it replaced scrolled
 * sideways, showed three and a half tabs, and nothing said Activity and Sell
 * were off to the right. From sm up, underlined tabs. The highlight slides to
 * the tab chosen (layoutId), and the arrow keys move along the row.
 */
function SectionTabs({ section, onSelect, versionCount }) {
  const reduceMotion = useReducedMotion();
  const refs = useRef({});
  const slide = reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 520, damping: 42 };

  const onKeyDown = (e) => {
    const at = SECTIONS.findIndex(s => s.key === section);
    const to = { ArrowRight: at + 1, ArrowLeft: at - 1, Home: 0, End: SECTIONS.length - 1 }[e.key];
    if (to === undefined) return;
    e.preventDefault();
    const next = SECTIONS[(to + SECTIONS.length) % SECTIONS.length].key;
    onSelect(next);
    refs.current[next]?.focus();
  };

  return (
    <div role="tablist" aria-label="Site dashboard" onKeyDown={onKeyDown}
      className="grid grid-cols-5 gap-1 p-1 rounded-2xl bg-zinc-900 border border-zinc-800 sm:flex sm:gap-1 sm:p-0 sm:rounded-none sm:bg-transparent sm:border-0 sm:border-b">
      {SECTIONS.map((s) => {
        const active = section === s.key;
        return (
          <button
            key={s.key}
            ref={(el) => { refs.current[s.key] = el; }}
            type="button"
            role="tab"
            id={`site-tab-${s.key}`}
            aria-selected={active}
            aria-controls="site-dashboard-panel"
            tabIndex={active ? 0 : -1}
            onClick={() => onSelect(s.key)}
            className={`relative flex flex-col items-center justify-center gap-1 h-14 min-w-0 rounded-xl text-[11px] font-semibold outline-none transition-colors focus-visible:ring-2 focus-visible:ring-orange-500 sm:flex-row sm:gap-1.5 sm:h-10 sm:px-3.5 sm:rounded-none sm:text-sm sm:-mb-px sm:focus-visible:ring-inset ${
              active ? 'text-white' : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            {active && (
              <>
                <motion.span layoutId="site-tab-tile" transition={slide} aria-hidden="true" className="absolute inset-0 rounded-xl bg-zinc-800 shadow-sm sm:hidden" />
                <motion.span layoutId="site-tab-line" transition={slide} aria-hidden="true" className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-orange-500 hidden sm:block" />
              </>
            )}
            <s.icon aria-hidden="true" className={`relative w-[18px] h-[18px] sm:w-4 sm:h-4 transition-colors ${active ? 'text-orange-400 sm:text-white' : ''}`} />
            <span className="relative leading-none sm:leading-normal">{s.label}</span>
            {s.key === 'versions' && versionCount > 0 && (
              <span className="absolute top-1.5 left-1/2 ml-2 min-w-[16px] h-4 px-1 rounded-full bg-orange-500 text-ink text-[9px] font-bold grid place-items-center sm:relative sm:top-auto sm:left-auto sm:ml-0 sm:h-auto sm:py-0.5 sm:px-1.5 sm:bg-zinc-800 sm:border sm:border-zinc-700 sm:text-zinc-400 sm:text-[10px] sm:font-normal">
                {versionCount}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default function SiteDashboard({
  websiteId, websiteCode, versionHistory, onRevert, onDownloadZip, canExport = false, unlock,
  navigate, onTitleSaved, onIconSaved, onShare, initialSection = 'overview',
}) {
  const reduceMotion = useReducedMotion();
  const [section, setSection] = useState(SECTIONS.some(s => s.key === initialSection) ? initialSection : 'overview');
  // Which way the last switch went, so the new section slides in from its side.
  const [direction, setDirection] = useState(0);
  const [meta, setMeta] = useState(null);
  const [copied, setCopied] = useState(false);
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingIcon, setEditingIcon] = useState(false);
  const scrollRef = useRef(null);
  const tabsAnchorRef = useRef(null);

  useEffect(() => {
    if (!websiteId) return undefined;
    let cancelled = false;
    authFetch(`/api/websites/${websiteId}`)
      .then(data => { if (!cancelled) setMeta(data.website); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [websiteId]);

  const shareUrl = meta?.is_public && meta?.slug
    ? `${window.location.origin}/s/${meta.slug}`
    : websiteId ? `${window.location.origin}/preview/${websiteId}` : '';

  const handleCopyLink = useCallback(() => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => toast.error('Could not copy.'));
  }, [shareUrl]);

  const handleRename = useCallback(async (title) => {
    try {
      const { website } = await authFetch(`/api/websites/${websiteId}`, { method: 'PATCH', body: JSON.stringify({ title }) });
      setMeta(m => ({ ...m, ...website }));
      onTitleSaved?.(title);
      toast.success('Renamed!');
    } catch (e) { toast.error(e.message); }
  }, [websiteId, onTitleSaved]);

  const handleToggleVisibility = useCallback(async (next) => {
    setSavingVisibility(true);
    try {
      // Publishing (not just flipping the flag) gives the site its address.
      const url = `/api/websites/${websiteId}/${next ? 'publish' : 'unpublish'}`;
      await authFetch(url, { method: 'POST', body: JSON.stringify({}) });
      const { website } = await authFetch(`/api/websites/${websiteId}`);
      setMeta(website);
      toast.success(next ? 'Your site is live! 🎉' : 'Site set to private.');
    } catch (e) { toast.error(e.message); }
    finally { setSavingVisibility(false); }
  }, [websiteId]);

  const handleDelete = useCallback(async () => {
    setDeleting(true);
    try {
      await authFetch(`/api/websites/${websiteId}`, { method: 'DELETE' });
      toast.success('Site deleted.');
      navigate('/dashboard?section=sites');
    } catch (e) { toast.error(e.message); setDeleting(false); }
  }, [websiteId, navigate]);

  const icon = meta?.icon_svg || websiteCode.icon || '';
  const handleIconSaved = useCallback((website) => {
    setMeta(m => ({ ...m, icon_svg: website.icon_svg, drawn_icon_svg: website.drawn_icon_svg ?? null }));
    // The workspace's top bar and preview read the icon from the workspace.
    onIconSaved?.(website.icon_svg);
  }, [onIconSaved]);
  const editIcon = useCallback(() => setEditingIcon(true), []);

  // A switch slides the new section in from the side its tab is on. With the
  // tabs stuck to the top, the page is brought back to the section's start,
  // or the new section would open somewhere down its own length.
  const selectSection = useCallback((key) => {
    if (key === section) return;
    const from = SECTIONS.findIndex(s => s.key === section);
    const to = SECTIONS.findIndex(s => s.key === key);
    setDirection(to > from ? 1 : -1);
    setSection(key);
    const box = scrollRef.current;
    const anchor = tabsAnchorRef.current;
    if (box && anchor) {
      const top = anchor.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop;
      if (box.scrollTop > top) box.scrollTo?.({ top, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
  }, [section, reduceMotion]);

  if (!websiteId) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center px-8 py-20">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin mb-4" />
        <h3 className="text-white font-semibold mb-1.5">Your site is on its way</h3>
        <p className="text-zinc-500 text-sm max-w-sm leading-relaxed">
          Once it is saved you can publish it, share it, manage its versions and sell it here.
        </p>
      </div>
    );
  }

  const slideX = reduceMotion ? 0 : 20;

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-4 pb-10 sm:py-6">
        <div className="flex items-center justify-between gap-3 mb-3 sm:mb-4">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-zinc-600 font-semibold">Site dashboard</p>
            <h2 className="text-lg font-bold text-white truncate">{meta?.title || websiteCode.title || 'Your site'}</h2>
          </div>
          <button onClick={() => navigate('/dashboard?section=sites')}
            className="shrink-0 inline-flex items-center gap-1.5 h-9 px-2 -mr-2 rounded-lg text-xs font-medium text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> All sites
          </button>
        </div>

        <div ref={tabsAnchorRef} aria-hidden="true" />
        {/* Stuck to the top while the section scrolls under it, so the other
            sections are one tap away from anywhere in a long one. */}
        <div className="sticky top-0 z-20 -mx-4 px-4 py-2 mb-4 bg-zinc-950/90 backdrop-blur-md sm:-mx-6 sm:px-6 sm:py-0 sm:mb-6">
          <SectionTabs section={section} onSelect={selectSection} versionCount={versionHistory?.length || 0} />
        </div>

        <AnimatePresence mode="wait" initial={false} custom={direction}>
          <motion.div
            key={section}
            role="tabpanel"
            id="site-dashboard-panel"
            aria-labelledby={`site-tab-${section}`}
            custom={direction}
            variants={{
              enter: (d) => ({ opacity: 0, x: d * slideX }),
              shown: { opacity: 1, x: 0 },
              leave: (d) => ({ opacity: 0, x: d * -slideX }),
            }}
            initial="enter"
            animate="shown"
            exit="leave"
            transition={{ duration: reduceMotion ? 0 : 0.16, ease: 'easeOut' }}
          >
            {section === 'overview' && (
              <OverviewSection
                meta={meta} websiteCode={websiteCode} shareUrl={shareUrl}
                onOpenApp={() => window.open(`/preview/${websiteId}`, '_blank', 'noopener')}
                onCopyLink={handleCopyLink} copied={copied}
                onDownloadZip={onDownloadZip} canExport={canExport} unlock={unlock}
                onRename={handleRename} onToggleVisibility={handleToggleVisibility} savingVisibility={savingVisibility}
                onShare={onShare} onEditIcon={editIcon}
              />
            )}
            {section === 'sell' && <SellSection websiteId={websiteId} meta={meta} websiteCode={websiteCode} />}
            {section === 'versions' && <VersionsSection versions={versionHistory} currentVersion={meta?.version} onRevert={onRevert} />}
            {section === 'activity' && <ActivitySection websiteId={websiteId} meta={meta} />}
            {section === 'settings' && (
              <SettingsSection meta={meta} icon={icon} onEditIcon={editIcon} onRename={handleRename} onToggleVisibility={handleToggleVisibility}
                savingVisibility={savingVisibility} onDelete={handleDelete} deleting={deleting} />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <SiteIconEditor
        open={editingIcon}
        onClose={() => setEditingIcon(false)}
        websiteId={websiteId}
        icon={icon}
        originalIcon={meta?.drawn_icon_svg || null}
        siteTitle={meta?.title || websiteCode.title || ''}
        onSaved={handleIconSaved}
      />
    </div>
  );
}
